import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import lgnBg from "../../Assets/Login/bgcolor.jpg";
import logo from "../../Assets/Logos/logov.png";
import { Eye, EyeOff } from "lucide-react";

const STEPS = { EMAIL: "email", RESET: "reset" };

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const showToast = (msg, type = "error") => {
    toast[type](msg, {
      position: "top-center",
      autoClose: type === "error" ? 3000 : 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "colored",
    });
  };

  const startCooldown = () => {
    setResendCooldown(30);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Step 1: Send OTP — only fires on explicit form submit
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (isLoading) return; // prevent double submit
    try {
      setIsLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/users/forgot-password-otp`,
        { email },
      );
      showToast("OTP sent to your email and mobile!", "success");
      startCooldown();
      setStep(STEPS.RESET);
    } catch (error) {
      showToast(error.response?.data?.error || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP + Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      showToast("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      showToast("Password must be at least 8 characters");
      return;
    }
    try {
      setIsLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/users/verify-otp-reset-password`,
        { email, otp, newPassword: password, confirmPassword },
      );
      showToast("Password reset successfully! Please LogIn again !", "success");
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      showToast(error.response?.data?.error || "Password reset failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP — only via explicit button click, with cooldown
  const handleResendOtp = async () => {
    if (resendCooldown > 0 || isLoading) return;
    try {
      setIsLoading(true);
      await axios.post(`${process.env.REACT_APP_API_URL}/users/resend-otp`, {
        email,
      });
      showToast("OTP resent!", "success");
      startCooldown();
      setOtp(""); // clear old OTP input
    } catch (error) {
      showToast(error.response?.data?.error || "Failed to resend OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = () => {
    clearInterval(cooldownRef.current);
    setStep(STEPS.EMAIL);
    setOtp("");
    setPassword("");
    setConfirmPassword("");
    setResendCooldown(0);
  };

  return (
    <div className="w-full min-h-screen grid grid-cols-1">
      <div
        className="flex w-full justify-center items-center p-10 bg-cover bg-center"
        style={{ backgroundImage: `url(${lgnBg})` }}
      >
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
          <div className="flex justify-center items-center pb-8">
            <img src={logo} alt="logo" className="w-15 h-15" />
          </div>

          {/* Step indicator */}
          <div className="flex justify-center gap-2 mb-6">
            {Object.values(STEPS).map((s, i) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all duration-300 ${
                  Object.values(STEPS).indexOf(step) >= i
                    ? "bg-primary w-8"
                    : "bg-gray-200 w-4"
                }`}
              />
            ))}
          </div>

          <h2 className="text-2xl font-semibold text-center mb-1">
            {step === STEPS.EMAIL ? "Forgot Password" : "Reset Password"}
          </h2>
          <p className="text-gray-500 text-center mb-6">
            {step === STEPS.EMAIL
              ? "Enter your registered email to receive an OTP"
              : `Enter the OTP sent to ${email} and your new password`}
          </p>

          {/* Step 1: Email */}
          {step === STEPS.EMAIL && (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full ${
                  isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-btnHover"
                } text-white py-2 rounded-md transition duration-200`}
              >
                {isLoading ? "Sending..." : "Send OTP"}
              </button>
            </form>
          )}

          {/* Step 2: OTP + New Password (single form, single submit) */}
          {step === STEPS.RESET && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <input
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 text-center tracking-widest text-lg"
                maxLength={6}
                required
              />

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2 text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-2 text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading || otp.length !== 6}
                className={`w-full ${
                  isLoading || otp.length !== 6
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-btnHover"
                } text-white py-2 rounded-md transition duration-200`}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>

              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={handleChangeEmail}
                  className="text-gray-500 hover:underline"
                >
                  ← Change email
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || isLoading}
                  className={`${
                    resendCooldown > 0 || isLoading
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:underline"
                  }`}
                >
                  {resendCooldown > 0
                    ? `Resend OTP in ${resendCooldown}s`
                    : "Resend OTP"}
                </button>
              </div>
            </form>
          )}

          <div className="text-center mt-4">
            <span className="text-gray-600">Remember your password? </span>
            <button
              onClick={() => navigate("/login")}
              className="text-blue-600 hover:underline"
            >
              Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
